import React, { useState } from "react";
import "./styles/PolicyPage.css";

function PolicyPage() {
  const [activeTab, setActiveTab] = useState("privacy");

  const tabs = [
    { id: "privacy", label: "Privacy Policy" },
    { id: "terms", label: "Terms & Conditions" },
    { id: "cookies", label: "Cookie Policy" },
  ];

  return (
    <div className="policy-page">
      <div className="policy-container">
        <div className="policy-tabs">
          {tabs.map((tab) => (
            <button
              key={tab.id}
              className={`tab-button ${activeTab === tab.id ? "active" : ""}`}
              onClick={() => setActiveTab(tab.id)}
            >
              {tab.label}
            </button>
          ))}
        </div>

        <div className="policy-content">
          {activeTab === "privacy" && (
            <div className="policy-section">
              <h1>Privacy Policy for Roambis Mobile (roambis.com)</h1>
              <p className="effective-date">Effective Date: 9/7/2025</p>

              <p className="intro">
                Roambis Mobile ("we," "our," or "us") is committed to protecting your privacy. This Privacy Policy explains how we collect, use, disclose, and protect your personal data when you visit our website (roambis.com), purchase an eSIM plan, or interact with our services.
              </p>
              
              <p className="intro">
                By using our website and services, you consent to the practices described in this policy.
              </p>

              <section>
                <h2>1. What Information We Collect</h2>
                <p>We collect the following types of information:</p>
                
                <h3>a. Personal Information</h3>
                <ul>
                  <li>Name</li>
                  <li>Email address</li>
                  <li>Phone number</li>
                  <li>Country of travel</li>
                  <li>Device type (for eSIM compatibility)</li>
                  <li>Billing address and payment information (processed via secure third parties)</li>
                </ul>

                <h3>b. Usage Data</h3>
                <ul>
                  <li>IP address</li>
                  <li>Browser type</li>
                  <li>Device identifiers</li>
                  <li>Pages visited and time spent on the website</li>
                  <li>Referral source</li>
                </ul>

                <h3>c. Cookies and Tracking Technologies</h3>
                <p>We use cookies and similar technologies to enhance user experience, analyze site traffic, and tailor marketing. You can manage cookie preferences via your browser settings.</p>
              </section>

              <section>
                <h2>2. How We Use Your Information</h2>
                <p>We use your information to:</p>
                <ul>
                  <li>Provide and activate eSIM services</li>
                  <li>Process orders and payments</li>
                  <li>Communicate with you about your account or services</li>
                  <li>Improve our website and customer experience</li>
                  <li>Send promotional offers, updates, or travel tips (you may opt out anytime)</li>
                  <li>Comply with legal obligations and prevent fraud</li>
                </ul>
              </section>

              <section>
                <h2>3. How We Share Your Information</h2>
                <p>We do not sell your personal data. We may share your information with:</p>
                <ul>
                  <li>Trusted third-party service providers (e.g. payment processors, email tools, analytics providers) to operate our services</li>
                  <li>Telecom partners to activate and deliver eSIM services</li>
                  <li>Legal authorities if required by law or to protect our rights</li>
                </ul>
                <p>All third-party partners are contractually bound to protect your information.</p>
              </section>

              <section>
                <h2>4. Data Retention</h2>
                <p>We retain your personal information only for as long as necessary to:</p>
                <ul>
                  <li>Provide you with services</li>
                  <li>Comply with legal obligations</li>
                  <li>Resolve disputes and enforce agreements</li>
                </ul>
                <p>You may request deletion of your data by contacting us at support@roambis.com.</p>
              </section>

              <section>
                <h2>5. Your Rights</h2>
                <p>Depending on your location (e.g. EU, UK, Nigeria), you may have the following rights:</p>
                <ul>
                  <li>Access the personal data we hold about you</li>
                  <li>Request correction or deletion of your data</li>
                  <li>Object to or restrict certain data processing</li>
                  <li>Request data portability</li>
                  <li>Withdraw consent at any time (if applicable)</li>
                </ul>
                <p>To exercise your rights, contact us at support@roambis.com.</p>
              </section>

              <section>
                <h2>6. Children's Privacy</h2>
                <p>Roambis does not knowingly collect personal data from children under the age of 16. If you believe a child has provided us with data, please contact us for removal.</p>
              </section>

              <section>
                <h2>7. International Transfers</h2>
                <p>Your information may be transferred and processed outside of your country of residence (e.g., in the EU, US, or Nigeria) where privacy laws may differ. We ensure adequate protection through standard contractual clauses or equivalent safeguards.</p>
              </section>

              <section>
                <h2>8. Security Measures</h2>
                <p>We implement appropriate technical and organizational security measures to protect your personal data from unauthorized access, loss, or misuse. These include HTTPS encryption, access controls, and regular security audits.</p>
              </section>

              <section>
                <h2>9. Third-Party Links</h2>
                <p>Our website may contain links to external sites (e.g., social media, partners). We are not responsible for the privacy practices or content of those third-party websites.</p>
              </section>

              <section>
                <h2>10. Changes to This Policy</h2>
                <p>We may update this Privacy Policy periodically. When we do, we'll revise the "Effective Date" at the top of this page. Significant changes will be communicated via email or website banner.</p>
              </section>

              <section>
                <h2>11. Contact Us</h2>
                <p>If you have any questions or concerns about this Privacy Policy or your personal data, please contact us at:</p>
                <div className="contact-info">
                  <p><strong>Roambis Mobile</strong></p>
                  <p>📧 Email: support@roambis.com</p>
                  <p>🌍 Website: https://roambis.com</p>
                </div>
              </section>
            </div>
          )}

          {activeTab === "terms" && (
            <div className="policy-section">
              <h1>Terms and Conditions for Roambis Mobile (roambis.com)</h1>
              <p className="effective-date">Effective Date: [Insert Date]</p>

              <p className="intro">
                Welcome to Roambis Mobile. These Terms and Conditions ("Terms") govern your use of our website roambis.com and our eSIM products and services. By accessing or using our site and services, you agree to be bound by these Terms. If you do not agree, please do not use our website or purchase our services.
              </p>

              <section>
                <h2>1. About Us</h2>
                <p>
                  Roambis Mobile provides digital eSIM plans that allow users to access mobile data services while traveling internationally. We operate under applicable telecom and digital services regulations and partner with global networks to deliver connectivity.
                </p>
              </section>

              <section>
                <h2>2. Eligibility</h2>
                <p>
                  You must be at least 16 years old to use our services. By using our site or making a purchase, you confirm that you meet this requirement.
                </p>
              </section>

              <section>
                <h2>3. Use of Services</h2>
                <p>When you purchase and activate a Roambis eSIM, you agree to:</p>
                <ul>
                  <li>Use the eSIM for lawful and personal purposes only</li>
                  <li>Not resell, share commercially, or tamper with the service</li>
                  <li>Follow any applicable usage restrictions in your selected destination</li>
                </ul>
                <p>We reserve the right to suspend or terminate your service for abuse, misuse, or fraudulent activity.</p>
              </section>

              <section>
                <h2>4. Ordering and Payment</h2>
                <ul>
                  <li>All payments are processed securely via trusted third-party providers (e.g., Stripe, PayPal).</li>
                  <li>Prices are displayed in your selected currency and may include taxes or fees based on location.</li>
                  <li>Once payment is complete and the eSIM is delivered (via QR code or email), the sale is considered final.</li>
                </ul>
              </section>

              <section>
                <h2>5. eSIM Activation & Compatibility</h2>
                <p>
                  It is your responsibility to ensure your device supports eSIM technology and is carrier-unlocked. We provide compatibility guidance, but cannot guarantee functionality on unsupported or locked devices.
                </p>
                <p>
                  If you are unsure, please contact support before purchasing.
                </p>
              </section>

              <section>
                <h2>6. Refund Policy</h2>
                <p>Roambis offers refunds only in the following cases:</p>
                <ul>
                  <li>eSIM was not delivered due to a technical issue</li>
                  <li>eSIM cannot be activated, and the issue is confirmed by our support team</li>
                  <li>Customer contacts support within 7 days of purchase with a valid issue</li>
                </ul>
                <p>Refunds are not granted for:</p>
                <ul>
                  <li>Incompatible or locked devices</li>
                  <li>Unused data after the validity period ends</li>
                  <li>User error during activation (without technical fault)</li>
                </ul>
                <p>For refund requests, email support@roambis.com.</p>
              </section>

              <section>
                <h2>7. Data Usage & Speed</h2>
                <p>
                  Data plans are subject to network availability and local conditions. While we aim to provide stable high-speed connections, actual speed and coverage may vary by country, network provider, and congestion.
                </p>
                <p>
                  Roambis is not responsible for performance issues due to factors beyond our control (e.g., tower outages, SIM compatibility, government restrictions).
                </p>
              </section>

              <section>
                <h2>8. Intellectual Property</h2>
                <p>
                  All content on roambis.com — including logos, brand materials, graphics, text, and code — is owned by Roambis Mobile or its partners. You may not copy, reproduce, or use any materials without written permission.
                </p>
              </section>

              <section>
                <h2>9. Limitation of Liability</h2>
                <p>To the fullest extent permitted by law:</p>
                <ul>
                  <li>Roambis Mobile is not liable for any indirect, incidental, or consequential damages arising from the use or inability to use our services.</li>
                  <li>Our total liability shall not exceed the amount paid for the specific eSIM product or service.</li>
                </ul>
              </section>

              <section>
                <h2>10. Modifications to Terms</h2>
                <p>
                  We may update these Terms at any time. When we do, we will revise the "Effective Date" at the top of this page. Continued use of our services after such changes constitutes your agreement to the new Terms.
                </p>
              </section>

              <section>
                <h2>11. Termination</h2>
                <p>We reserve the right to suspend or terminate your access to Roambis services at our discretion, especially in cases of:</p>
                <ul>
                  <li>Misuse or abuse of the service</li>
                  <li>Fraudulent activity</li>
                  <li>Violation of these Terms</li>
                </ul>
              </section>

              <section>
                <h2>12. Governing Law</h2>
                <p>
                  These Terms are governed by the laws of [insert jurisdiction – e.g., Nigeria, United Kingdom, or Delaware, USA, depending on your company registration]. Any disputes will be subject to the exclusive jurisdiction of the courts in that region.
                </p>
              </section>

              <section>
                <h2>13. Contact Us</h2>
                <p>For questions, support, or legal inquiries, please contact:</p>
                <div className="contact-info">
                  <p><strong>Roambis Mobile</strong></p>
                  <p>📧 Email: support@roambis.com</p>
                  <p>🌍 Website: https://roambis.com</p>
                </div>
              </section>
            </div>
          )}

          {activeTab === "cookies" && (
            <div className="policy-section">
              <h1>✅ Cookie Policy for Roambis Mobile</h1>
              <p className="effective-date">Effective Date: 9/7/2025</p>

              <p className="intro">
                At Roambis Mobile, we use cookies and similar technologies to improve your browsing experience, understand user behavior, and personalize content and ads.
              </p>
              
              <p className="intro">
                By continuing to browse our site, you consent to the use of cookies in accordance with this Cookie Policy.
              </p>

              <section>
                <h2>1. What Are Cookies?</h2>
                <p>
                  Cookies are small text files placed on your device when you visit a website. They help us recognize you and remember your preferences across sessions.
                </p>
              </section>

              <section>
                <h2>2. Types of Cookies We Use</h2>
                
                <h3>Essential Cookies:</h3>
                <p>Required for the site to function (e.g., shopping cart, login, eSIM activation).</p>
                
                <h3>Performance Cookies:</h3>
                <p>Help us analyze how users interact with the website (e.g., Google Analytics).</p>
                
                <h3>Functionality Cookies:</h3>
                <p>Store your preferences (e.g., language, region).</p>
                
                <h3>Marketing Cookies:</h3>
                <p>Used to deliver targeted ads and track the effectiveness of our campaigns (e.g., Facebook Pixel, Google Ads).</p>
              </section>

              <section>
                <h2>3. How to Control Cookies</h2>
                <p>
                  You can manage or delete cookies through your browser settings. However, disabling certain cookies may affect website functionality.
                </p>
                <p>For detailed guidance, visit:</p>
                <ul>
                  <li>www.aboutcookies.org</li>
                  <li>www.allaboutcookies.org</li>
                </ul>
              </section>

              <section>
                <h2>4. Third-Party Cookies</h2>
                <p>
                  Some cookies are placed by trusted third parties like analytics or advertising providers. We do not control these cookies, but we ensure that providers meet data privacy standards.
                </p>
              </section>

              <section>
                <h2>5. Updates to This Policy</h2>
                <p>
                  We may update this policy from time to time. Changes will be posted on this page with a new effective date.
                </p>
              </section>

              <section>
                <h2>Questions?</h2>
                <p>Contact us at support@roambis.com</p>
              </section>
            </div>
          )}
        </div>
      </div>
    </div>
  );
}

export default PolicyPage;
